/**
 * The integration detail page's two-column grid (CC 0526).
 *
 * Two columns, minmax(0, 1.6fr) and minmax(0, 1fr), from a 1100px container width up. Below that, one column:
 * the left content first, then the right column (scopes, then Recent activity), in source order. The width is
 * the grid's own (ResizeObserver), not the window's, so the settings rail and any side panel count.
 * minmax(0, …) lets each track shrink below its content's min-content, so neither column can widen the page.
 */
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

export const DETAIL_GRID_STACK_BELOW = 1100;

export function detailGridColumns(width: number): string {
  return width > 0 && width < DETAIL_GRID_STACK_BELOW ? "minmax(0, 1fr)" : "minmax(0, 1.6fr) minmax(0, 1fr)";
}

export function DetailGrid({ children, style, ...rest }: { children?: ReactNode; style?: CSSProperties } & Record<string, unknown>) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => setWidth(el.getBoundingClientRect().width);
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const columns = detailGridColumns(width);
  return (
    <div ref={ref} data-columns={columns.includes("1.6fr") ? "2" : "1"} {...rest} style={{ ...style, display: "grid", gridTemplateColumns: columns }}>
      {children}
    </div>
  );
}
